import { useMemo } from 'react'
import { COMMENT_THREAD_STYLE } from '@hub/contracts'
import type { DocumentCommentsState } from '@/lib/documents/use-document-comments'

/**
 * Colours the range highlights of the *open* threads (PLAN margin comments
 * §3, risk table): a stray span with an unknown id renders as plain text,
 * resolved threads only show while "Visa lösta" is on, and the active /
 * hovered thread gets the stronger colour. A stylesheet beats a decoration
 * pass here: ProseMirror re-creates span DOM on transactions, CSS survives.
 */
export function CommentHighlightStyle({ state }: { state: DocumentCommentsState }) {
  const { hidden, openThreads, resolvedThreads, showResolved, composer, hoverId } = state
  const activeId = state.activeId === 'new' ? (composer?.threadId ?? null) : state.activeId
  const openIds = useMemo(
    () =>
      hidden
        ? []
        : openThreads.map((th) => th.id).concat(composer?.threadId ? [composer.threadId] : []),
    [hidden, openThreads, composer]
  )
  const resolvedIds = useMemo(
    () => (hidden || !showResolved ? [] : resolvedThreads.map((th) => th.id)),
    [hidden, showResolved, resolvedThreads]
  )
  const css = useMemo(() => {
    if (hidden) return ''
    const sel = (id: string) =>
      `.hub-block-editor [data-style-type="${COMMENT_THREAD_STYLE}"][data-value="${CSS.escape(id)}"]`
    const rules: string[] = []
    if (openIds.length > 0) rules.push(`${openIds.map(sel).join(',')}{${HIGHLIGHT}}`)
    if (resolvedIds.length > 0) rules.push(`${resolvedIds.map(sel).join(',')}{${RESOLVED}}`)
    // Only visible threads get the strong colour (a card unmounting under the
    // pointer leaves a stale hover id behind).
    const visible = new Set([...openIds, ...resolvedIds])
    const strong = [activeId, hoverId].filter((id): id is string => !!id && visible.has(id))
    if (strong.length > 0) rules.push(`${strong.map(sel).join(',')}{${ACTIVE}}`)
    return rules.join('\n')
  }, [hidden, openIds, resolvedIds, activeId, hoverId])
  return <style data-comment-highlights>{css}</style>
}

const HIGHLIGHT =
  'background:var(--comment-highlight);box-shadow:inset 0 -2px 0 var(--comment-highlight-line);cursor:pointer;border-radius:2px'
const RESOLVED =
  'background:var(--comment-highlight-resolved);box-shadow:inset 0 -2px 0 var(--comment-highlight-resolved);cursor:pointer;border-radius:2px'
const ACTIVE =
  'background:var(--comment-highlight-active);box-shadow:inset 0 -2px 0 var(--comment-highlight-line-active)'
